import { Link } from '../../../../widgets/buttons';
import { PageHeaderContainer } from '../../../../widgets/pageHeader';
import { Select } from '../../../../widgets/select';
import { SelectionType, SelectionTypeContainer } from '../../../../widgets/selectionType';
import { VerticalLayout } from '../../../../widgets/layouts';
import { useDialogManager } from '../../../../widgets/dialog';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import { Navigate, useNavigate } from 'react-router-dom';
import { generateStoreNumbers } from 'src/components/pages/salesChannels/helpers';
import { T, backofficeUrl, getUrlWithQueryParams } from 'src/shared/helpers';
import BookIcon from 'src/shared/icons/book-open-solid.svg';
import ExternalLinkIcon from 'src/shared/icons/external-link-alt-solid.svg';
import AddProductNoEcommerce from 'src/shared/icons/no-ecommerce.svg';
import AddProductFromProvider from 'src/shared/icons/pod.svg';
import AddProductFromZakeke from 'src/shared/icons/store.svg';
import styled from 'styled-components';
import { NoIntegrationDialog } from '../../../../../shared/dialogs';
import {
    modeAction,
    newProductNoIntegrationAction,
    setHeaderDescriptionCustomComponent,
    setSalesChannelsAction,
    useProductConfigurationChangeStep,
    useProductConfigurationState
} from '../../state';
import pages from '../../state/pages';
import { addProductQuery } from './__generated__/addProductQuery.graphql';

// --- Layout ---

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
    margin: 0 0 16px 0;
`;

// --- Sales Channel Select ---

const ChannelSelectWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
`;

const ChannelSelectLabel = styled.label`
    font-size: 14px;
    font-weight: 500;
    color: ${(props) => props.theme.app.textColor};
    white-space: nowrap;
`;

const StyledSelect = styled(Select)`
    max-width: 400px;
`;

const SourceCard = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 12px;
    background: #ffffff;
    padding: 24px;
`;

// --- From Scratch ---

const ScratchCard = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 24px;
    border: 2px dashed ${(props) => props.theme.app.borderColor};
    border-radius: 12px;
    background: ${(props) => props.theme.app.backgroundColor};
    cursor: pointer;
    transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;

    &:hover {
        border-color: ${(props) => props.theme.app.secondary};
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    }
`;

const ScratchIconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: #ffffff;
    flex-shrink: 0;

    svg {
        width: 32px;
        height: 32px;
    }
`;

const ScratchContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ScratchTitle = styled.span`
    font-weight: 600;
    font-size: 15px;
    color: ${(props) => props.theme.app.textColor};
`;

const ScratchDescription = styled.span`
    font-size: 13px;
    color: ${(props) => props.theme.app.textColor};
`;

// --- Guides ---

const GuidesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 12px;
`;

const GuideCard = styled.a`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 10px;
    background: #ffffff;
    text-decoration: none;
    color: ${(props) => props.theme.app.textColor};
    transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;

    &:hover {
        border-color: ${(props) => props.theme.app.secondary};
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }
`;

const GuideIconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: ${(props) => props.theme.app.backgroundColor};
    flex-shrink: 0;

    svg {
        width: 16px;
        height: 16px;
        fill: ${(props) => props.theme.app.secondary};
    }
`;

const GuideText = styled.span`
    font-size: 13px;
    font-weight: 500;
    line-height: 1.4;
    flex: 1;
    min-width: 0;
`;

const GuideLinkIcon = styled.div`
    flex-shrink: 0;
    svg {
        width: 12px;
        height: 12px;
        fill: ${(props) => props.theme.app.textColor};
    }
`;

// --- Component ---

const AddProduct: FunctionComponent<{}> = () => {
    const { state, dispatch } = useProductConfigurationState();
    const changeStep = useProductConfigurationChangeStep();
    const { showDialog, closeDialog } = useDialogManager();
    const navigate = useNavigate();

    const query = useLazyLoadQuery<addProductQuery>(
        graphql`
            query addProductQuery {
                ecommerceSettings {
                    ecommerceName
                    hasProductsApi
                }
                salesChannels(status: ACTIVE) {
                    id
                    detail {
                        id
                        properties {
                            name
                        }
                    }
                    ecommerceSettings {
                        ecommerceName
                        remoteUrl
                        hasProductsApi
                        definitiveUrl
                    }
                    supportedProviders
                }
            }
        `,
        {}
    );

    // Sales channel for ecommerces integrated
    const realSalesChannels = query.salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');

    // Default sales channel (No ecommerce)
    const noEcommerceSalesChannel = query.salesChannels.find((s) => s.ecommerceSettings?.ecommerceName === 'NONE');

    const salesChannelIDs = realSalesChannels.map((s) => s.id);
    const storeNumbers = generateStoreNumbers(salesChannelIDs);

    const [selectedChannelId, setSelectedChannelId] = useState<string>(realSalesChannels.length > 0 ? realSalesChannels[0].id : '');
    const selectedChannel = realSalesChannels.find((s) => s.id === selectedChannelId) ?? realSalesChannels[0];

    const getChannelLabel = (salesChannel: (typeof query)['salesChannels'][0]) => {
        const storeNumber = storeNumbers.find((x) => x.salesChannelID === salesChannel.id)?.storeNumber || 1;
        const name = salesChannel.detail?.properties?.name || '';
        const ecommerce = salesChannel.ecommerceSettings?.ecommerceName || '';
        const parts = [`Store ${storeNumber}`];
        if (name) parts.push(name);
        if (ecommerce && ecommerce !== 'NONE') parts.push(`(${ecommerce.replace(/_/g, ' ')})`);
        return parts.join(' \u2014 ');
    };

    if (
        query.salesChannels.length == 1 &&
        realSalesChannels.length == 1 &&
        realSalesChannels[0].supportedProviders.length === 0 &&
        realSalesChannels[0].ecommerceSettings?.ecommerceName != 'NONE'
    ) {
        // API redirects to CSV import
        if (query.ecommerceSettings?.ecommerceName === 'API' && !query.ecommerceSettings?.hasProductsApi) {
            navigate(backofficeUrl('/tools/CSVImporter'));
            return null;
        } else {
            dispatch(setSalesChannelsAction(realSalesChannels[0].id, [realSalesChannels[0].ecommerceSettings!.ecommerceName]));
            dispatch(modeAction('classic'));
            return <Navigate to={getUrlWithQueryParams(pages.find((x) => x.key === 'connect-catalog')!.url)} />;
        }
    }

    // Page title
    useEffect(() => {
        dispatch(
            setHeaderDescriptionCustomComponent(
                <PageHeaderContainer.TitleDescription>
                    <PageHeaderContainer.Title>{T._('Catalog', 'Admin')}</PageHeaderContainer.Title>
                </PageHeaderContainer.TitleDescription>
            )
        );

        return () => {
            dispatch(setHeaderDescriptionCustomComponent(null));
        };
    }, []);

    const hasStoreImport = (salesChannel: (typeof query)['salesChannels'][0]) =>
        salesChannel.ecommerceSettings?.ecommerceName !== 'API' ||
        (salesChannel.ecommerceSettings.ecommerceName === 'API' && salesChannel.ecommerceSettings?.hasProductsApi);

    const renderStoreOption = (salesChannel: (typeof query)['salesChannels'][0]) => {
        if (!hasStoreImport(salesChannel)) return null;
        return (
            <SelectionType
                key='store'
                tip={
                    <>
                        {T._('Pick a product from your store and make it customizable.', 'Admin')}
                        <br />
                        <br />
                        <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/360026668693#h_01GH9HN48ZE1GXZ0HYQJNWWQGP'>
                            {T._('Learn here how to do it.', 'Admin')}
                        </Link>
                    </>
                }
                isPod
                label={T._('Pick a product from your store', 'Admin')}
                icon={<AddProductFromZakeke />}
                onClick={() => {
                    dispatch(setSalesChannelsAction(salesChannel.id, [salesChannel.ecommerceSettings!.ecommerceName]));
                    dispatch(modeAction('classic'));
                    changeStep('connect-catalog');
                }}
            />
        );
    };

    const renderPodOption = (salesChannel: (typeof query)['salesChannels'][0]) => {
        if (salesChannel.supportedProviders.length === 0) return null;
        return (
            <SelectionType
                key='pod'
                tip={
                    <>
                        {T._('Access third-party product catalogs to sell drop-shipped customizable products.', 'Admin')}
                        <br />
                        <br />
                        <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/360026668693#h_01GH9KKJV4QC09RBBFRBVDJPEN'>
                            {T._('Learn more here.', 'Admin')}
                        </Link>
                    </>
                }
                isPod
                label={T._('Start from a POD service', 'Admin')}
                icon={<AddProductFromProvider />}
                onClick={() => {
                    dispatch(setSalesChannelsAction(salesChannel.id, [salesChannel.ecommerceSettings!.ecommerceName]));
                    dispatch(modeAction('pod'));
                    changeStep('supplier-selection');
                }}
            />
        );
    };

    const guides = [
        {
            label: T._('How to create your customizable product', 'Admin'),
            href: 'https://zakeke.zendesk.com/hc/en-us/articles/360026668693',
            track: 'Documentation How to create your customizable product'
        },
        {
            label: T._('Product with only text option', 'Admin'),
            href: 'https://zakeke.zendesk.com/hc/en-us/articles/360017399120',
            track: 'Documentation Product with only text option'
        },
        {
            label: T._('Rounded items (mugs, cans, tumblers, bottles, cups, glasses, ...)', 'Admin'),
            href: 'https://zakeke.zendesk.com/hc/en-us/articles/4863653329820',
            track: 'Documentation Rounded items (mugs, cans, tumblers, bottles, cups, glasses, ...)'
        },
        {
            label: T._('Sublimated sport jersey', 'Admin'),
            href: 'https://zakeke.zendesk.com/hc/en-us/articles/4407393227538',
            track: 'Documentation How to create sublimated sport jersey'
        },
        {
            label: T._('Double-sided product with the option to have the same design elements on each side', 'Admin'),
            href: 'https://zakeke.zendesk.com/hc/en-us/articles/4687256732828',
            track: 'Documentation Double-sided product with the option to have the same design elements on each side'
        }
    ];

    return (
        <VerticalLayout>
            <PageContainer>
                {/* --- Channel-specific sources --- */}
                <div>
                    <SectionTitle>{T._("Choose where you'd like to get it from", 'Admin')}</SectionTitle>

                    {realSalesChannels.length === 0 && query.salesChannels.length > 0 && (
                        <SelectionTypeContainer key={query.salesChannels[0].id}>
                            <SelectionType
                                key='store'
                                tip={
                                    <>
                                        {T._('Pick a product from your store and make it customizable.', 'Admin')}
                                        <br />
                                        <br />
                                        <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/360026668693#h_01GH9HN48ZE1GXZ0HYQJNWWQGP'>
                                            {T._('Learn here how to do it.', 'Admin')}
                                        </Link>
                                    </>
                                }
                                isPod
                                label={T._('Pick a product from your store', 'Admin')}
                                icon={<AddProductFromZakeke />}
                                onClick={() => {
                                    showDialog(
                                        'complete-ecommerce-integration',
                                        <NoIntegrationDialog
                                            subTitleNoIntegration={T._('Ready to integrate your catalog and make your products customizable?', 'Admin')}
                                            message={
                                                <label>
                                                    {T._(
                                                        'You need to connect your store in order to pick a product, make it customizable via Zakeke and sell it.',
                                                        'Admin'
                                                    )}
                                                </label>
                                            }
                                        ></NoIntegrationDialog>
                                    );
                                }}
                            />
                            <SelectionType
                                key='pod'
                                tip={
                                    <>
                                        {T._('Access third-party product catalogs to sell drop-shipped customizable products.', 'Admin')}
                                        <br />
                                        <br />
                                        <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/360026668693#h_01GH9KKJV4QC09RBBFRBVDJPEN'>
                                            {T._('Learn more here.', 'Admin')}
                                        </Link>
                                    </>
                                }
                                isPod
                                label={T._('Start from a POD service', 'Admin')}
                                icon={<AddProductFromProvider />}
                                onClick={() => {
                                    showDialog(
                                        'complete-ecommerce-integration',
                                        <NoIntegrationDialog
                                            subTitleNoIntegration={T._('Do you want to integrate your third-party catalog?', 'Admin')}
                                            message={
                                                <div>
                                                    <label>
                                                        {T._(
                                                            'In order to sell customizable products from third-party catalogs, you need to connect your store first. Please note that this option is only valid if your store is built on one of the following platforms:',
                                                            'Admin'
                                                        )}
                                                    </label>
                                                    <ul>
                                                        <li>Shopify</li>
                                                        <li>WooCommerce</li>
                                                        <li>BigCommerce</li>
                                                    </ul>
                                                    <a
                                                        href='https://zakeke.zendesk.com/hc/en-us/articles/360026668693-Create-a-customizable-product#h_01GH9KKJV4QC09RBBFRBVDJPEN'
                                                        target='_blank'
                                                    >
                                                        {T._('Read more here.', 'Admin')}
                                                    </a>
                                                </div>
                                            }
                                        ></NoIntegrationDialog>
                                    );
                                }}
                            />
                        </SelectionTypeContainer>
                    )}

                    {realSalesChannels.length >= 1 && selectedChannel && (
                        <SourceCard>
                            {realSalesChannels.length > 1 && (
                                <ChannelSelectWrapper>
                                    <ChannelSelectLabel>{T._('Store', 'Admin')}</ChannelSelectLabel>
                                    <StyledSelect value={selectedChannelId} onChange={(value: string) => setSelectedChannelId(value)}>
                                        {realSalesChannels.map((sc) => (
                                            <option key={sc.id} value={sc.id}>
                                                {getChannelLabel(sc)}
                                            </option>
                                        ))}
                                    </StyledSelect>
                                </ChannelSelectWrapper>
                            )}
                            <SelectionTypeContainer key={selectedChannel.id}>
                                {renderStoreOption(selectedChannel)}
                                {renderPodOption(selectedChannel)}
                            </SelectionTypeContainer>
                        </SourceCard>
                    )}
                </div>

                {/* --- From Scratch (cross-channel) --- */}
                {noEcommerceSalesChannel && (
                    <div>
                        <SectionTitle>{T._('Or start from scratch', 'Admin')}</SectionTitle>
                        <ScratchCard
                            onClick={() => {
                                dispatch(setSalesChannelsAction(noEcommerceSalesChannel.id, [noEcommerceSalesChannel.ecommerceSettings!.ecommerceName]));
                                dispatch(modeAction('classic'));
                                dispatch(newProductNoIntegrationAction());
                                changeStep('print-area');
                            }}
                        >
                            <ScratchIconContainer>
                                <AddProductNoEcommerce />
                            </ScratchIconContainer>
                            <ScratchContent>
                                <ScratchTitle>{T._('Create a product from scratch', 'Admin')}</ScratchTitle>
                                <ScratchDescription>
                                    {T._('Create a customizable product from scratch and connect it with your store later.', 'Admin')}
                                </ScratchDescription>
                            </ScratchContent>
                        </ScratchCard>
                    </div>
                )}

                {/* --- Guides --- */}
                <div>
                    <SectionTitle>{T._('Guides & resources', 'Admin')}</SectionTitle>
                    <GuidesGrid>
                        {guides.map((guide) => (
                            <GuideCard key={guide.href} href={guide.href} target='_blank' data-track={guide.track}>
                                <GuideIconContainer>
                                    <BookIcon />
                                </GuideIconContainer>
                                <GuideText>{guide.label}</GuideText>
                                <GuideLinkIcon>
                                    <ExternalLinkIcon />
                                </GuideLinkIcon>
                            </GuideCard>
                        ))}
                    </GuidesGrid>
                </div>
            </PageContainer>
        </VerticalLayout>
    );
};

export default AddProduct;
